<div class="sla-container">
  <div class="sla-header">
    <div>
      <h2><i class="fa-solid fa-user-graduate"></i> Skills</h2>
      <p class="sla-sub">Define skills, map them to the ticket types that require them, and set each agent's proficiency. The SkillBased auto-assign mode routes tickets to the most proficient available agent (falling back to least-load).</p>
    </div>
    <button class="btn-primary" (click)="startCreate()" *ngIf="!isCreating">
      <i class="fa-solid fa-plus"></i> New Skill
    </button>
  </div>

  <div class="banner banner-error" *ngIf="errorMessage">{{ errorMessage }}</div>
  <div class="banner banner-success" *ngIf="successMessage">{{ successMessage }}</div>

  <!-- Create -->
  <div class="policy-card editing" *ngIf="isCreating">
    <div class="form-grid">
      <label>Name<input type="text" [(ngModel)]="createForm.name" placeholder="e.g. Billing, Spanish, API Support" /></label>
      <label>Status
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </label>
      <label class="full-row">Description<input type="text" [(ngModel)]="createForm.description" placeholder="Optional" /></label>
    </div>
    <div class="form-actions">
      <button class="btn-primary" (click)="saveCreate()" [disabled]="isSaving">Create</button>
      <button class="btn-ghost" (click)="cancelCreate()">Cancel</button>
    </div>
  </div>

  <div class="sla-loading" *ngIf="isLoading"><i class="fa-solid fa-spinner fa-spin"></i> Loading skills…</div>

  <div class="sla-empty" *ngIf="!isLoading && skills.length === 0 && !isCreating">
    <i class="fa-solid fa-user-graduate"></i>
    <p>No skills yet. Create one, map it to issue types, and set agent proficiencies to enable skills-based routing.</p>
  </div>

  <!-- Skill list -->
  <div class="policy-card" [class.editing]="d.isEditing" *ngFor="let d of skills">
    <!-- Read row -->
    <div class="policy-row" *ngIf="!d.isEditing">
      <button class="icon-btn expand-btn" (click)="toggleExpand(d)" [title]="d.expanded ? 'Collapse' : 'Expand'">
        <i class="fa-solid" [class.fa-chevron-right]="!d.expanded" [class.fa-chevron-down]="d.expanded"></i>
      </button>
      <div class="policy-main">
        <span class="policy-name">{{ d.skill.Name }}</span>
        <span class="policy-meta">
          <span class="metric"><i class="fa-solid fa-tags"></i> {{ d.typeMaps.length }} type(s)</span>
          <span class="metric"><i class="fa-solid fa-users"></i> {{ d.proficiencies.length }} agent(s)</span>
        </span>
      </div>
      <span class="status-chip" [class.inactive]="d.skill.Status !== 'Active'">{{ d.skill.Status }}</span>
      <div class="policy-actions">
        <button class="icon-btn" title="Edit" (click)="startEdit(d)"><i class="fa-solid fa-pen"></i></button>
        <button class="icon-btn danger" title="Delete" (click)="deleteSkill(d)"><i class="fa-solid fa-trash"></i></button>
      </div>
    </div>

    <!-- Edit form -->
    <div *ngIf="d.isEditing">
      <div class="form-grid">
        <label>Name<input type="text" [(ngModel)]="d.form.name" /></label>
        <label>Status
          <select [(ngModel)]="d.form.status">
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </label>
        <label class="full-row">Description<input type="text" [(ngModel)]="d.form.description" placeholder="Optional" /></label>
      </div>
      <div class="form-actions">
        <button class="btn-primary" (click)="saveEdit(d)" [disabled]="isSaving">Save</button>
        <button class="btn-ghost" (click)="cancelEdit(d)">Cancel</button>
      </div>
    </div>

    <!-- Expanded: mappings + proficiencies -->
    <div class="skill-detail" *ngIf="d.expanded && !d.isEditing">
      <!-- Issue-type mappings -->
      <div class="rule-section">
        <div class="rule-section-head"><span class="rule-section-title">Required by ticket types</span></div>
        <div class="chip-list" *ngIf="d.typeMaps.length">
          <span class="map-chip" *ngFor="let m of d.typeMaps">
            {{ typeName(m.IssueTypeID) }}<span class="map-min" *ngIf="m.MinProficiency"> · min {{ m.MinProficiency }}</span>
            <button class="chip-x" title="Remove" (click)="removeTypeMap(d, m)"><i class="fa-solid fa-xmark"></i></button>
          </span>
        </div>
        <div class="inline-add">
          <select [(ngModel)]="d.newTypeId">
            <option value="">— Issue type —</option>
            <option *ngFor="let t of issueTypes" [value]="t.ID">{{ t.Name }}</option>
          </select>
          <select [(ngModel)]="d.newTypeMin">
            <option [ngValue]="null">Any proficiency</option>
            <option *ngFor="let l of levels" [ngValue]="l">Min {{ l }}</option>
          </select>
          <button class="btn-ghost btn-sm" (click)="addTypeMap(d)" [disabled]="isSaving"><i class="fa-solid fa-plus"></i> Map type</button>
        </div>
      </div>

      <!-- Per-person proficiency -->
      <div class="rule-section">
        <div class="rule-section-head"><span class="rule-section-title">Agent proficiency</span></div>
        <div class="prof-row" *ngFor="let p of d.proficiencies">
          <span class="prof-name">{{ personName(p.PersonID) }}</span>
          <select [ngModel]="p.ProficiencyLevel" (ngModelChange)="updateProficiency(p, $event)">
            <option *ngFor="let l of levels" [ngValue]="l">Level {{ l }}</option>
          </select>
          <button class="icon-btn danger" title="Remove" (click)="removeProficiency(d, p)"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="inline-add">
          <select [(ngModel)]="d.newPersonId">
            <option value="">— Person —</option>
            <option *ngFor="let pp of people" [value]="pp.ID">{{ personName(pp.ID) }}</option>
          </select>
          <select [(ngModel)]="d.newPersonLevel">
            <option *ngFor="let l of levels" [ngValue]="l">Level {{ l }}</option>
          </select>
          <button class="btn-ghost btn-sm" (click)="addProficiency(d)" [disabled]="isSaving"><i class="fa-solid fa-plus"></i> Add agent</button>
        </div>
      </div>
    </div>
  </div>
</div>
